import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { fullErrorText } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LoaderIcon } from "@/components/icons";
import { UserName } from "@/components/UserName";
import { cortexApi } from "@/api/cortex.api";
import { type Skill, SkillStatus } from "@/proto/agentplatform/v1/skill_pb";

function getSkillVersionCode(skill: Skill): number {
  const versions = skill.versions || [];
  const stable = versions.find(v => v.isStable);
  const latest = versions.find(v => v.isLatest);
  const target = stable || latest || versions[0];
  return target?.versionCode ?? 0;
}

const PAGE_SIZE = 12;

export default function SkillsPlazaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const [forkingId, setForkingId] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const forkMutation = useMutation({
    mutationFn: (skill: Skill) => {
      setForkingId(skill.id);
      return cortexApi.forkSkill({
        skillId: skill.id,
        skillVersionCode: getSkillVersionCode(skill),
      });
    },
    onSuccess: forked => {
      queryClient.invalidateQueries({ queryKey: ["skills-management"] });
      toast.success("Skill 克隆成功");
      navigate(`/plugins/skills/management/${forked.id}`);
    },
    onError: error => toast.error(fullErrorText(error)),
    onSettled: () => {
      setForkingId(null);
    },
  });

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ["skills-plaza-infinite", keyword],
      queryFn: ({ pageParam }) =>
        cortexApi.listSkills({
          page: pageParam,
          pageSize: PAGE_SIZE,
          keyword: keyword.trim(),
          withVersions: true,
          public: true,
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        if ((lastPage.data || []).length < PAGE_SIZE) return undefined;
        return lastPageParam + 1;
      },
    });

  const skills = data?.pages.flatMap(page => page.data || []) ?? [];
  const total = data?.pages[0]?.total || 0;
  const hasMore = hasNextPage;

  const lastCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver(
        entries => {
          if (entries[0]?.isIntersecting && hasMore) {
            fetchNextPage();
          }
        },
        { threshold: 0.1 },
      );
      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasMore, fetchNextPage],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Skills 广场</h2>
          <p className="text-sm text-muted-foreground mt-1">浏览和发现所有可用 Skills。</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Input
          className="max-w-sm"
          placeholder="搜索 Skill 名称或描述"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
        />
        {keyword && (
          <Button variant="ghost" size="sm" onClick={() => setKeyword("")}>
            清除
          </Button>
        )}
        <span className="text-sm text-muted-foreground">共 {total} 个 Skill</span>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="rounded-2xl border border-border/60 bg-card p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Skeleton className="h-2 w-2 rounded-full" />
                <Skeleton className="h-4 w-28" />
              </div>
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
              <div className="flex gap-1.5">
                <Skeleton className="h-5 w-12 rounded-md" />
                <Skeleton className="h-5 w-16 rounded-md" />
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-border/40">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="py-10 text-center space-y-3">
            <p className="text-sm text-destructive">加载失败，请重试。</p>
            <Button onClick={() => refetch()}>重试</Button>
          </CardContent>
        </Card>
      ) : skills.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-muted-foreground">暂无 Skill 数据。</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
          {skills.map((skill, index) => {
            const isLast = index === skills.length - 1;
            return (
              <div
                key={skill.id}
                ref={isLast ? lastCardRef : undefined}
                className="group relative flex flex-col p-5 rounded-2xl border border-border/60 bg-card hover:border-primary/20 hover:shadow-md transition-all duration-200 cursor-pointer"
                onClick={() => navigate(`/plugins/skills/plaza/${skill.id}`)}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                      skill.status === SkillStatus.AVAILABLE
                        ? "bg-emerald-500"
                        : skill.status === SkillStatus.UPLOADING
                          ? "bg-amber-500"
                          : "bg-destructive"
                    }`}
                  />
                  <h3 className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                    {skill.name}
                  </h3>
                </div>

                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 flex-1 mb-4">
                  {skill.description || "暂无仓库描述"}
                </p>

                {(skill.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {skill.tags.slice(0, 3).map(tag => (
                      <span
                        key={tag}
                        className="inline-block px-2 py-0.5 text-[11px] rounded-md bg-secondary text-secondary-foreground/60"
                      >
                        {tag}
                      </span>
                    ))}
                    {skill.tags.length > 3 && (
                      <span className="inline-block px-2 py-0.5 text-[11px] rounded-md bg-secondary text-secondary-foreground/40">
                        +{skill.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between mt-auto pt-3 border-t border-border/40">
                  <span className="text-[11px] text-muted-foreground/60">
                    上传者：
                    <UserName userId={skill.userId} />
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/40 hover:text-primary transition-colors"
                      onClick={e => {
                        e.stopPropagation();
                        forkMutation.mutate(skill);
                      }}
                      disabled={forkingId === skill.id}
                      title="克隆此 Skill"
                    >
                      {forkingId === skill.id ? (
                        <LoaderIcon className="h-3 w-3" />
                      ) : (
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                          />
                        </svg>
                      )}
                      克隆
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && !isError && skills.length > 0 && (
        <div className="flex items-center justify-center py-4">
          {isFetchingNextPage ? (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <LoaderIcon className="h-4 w-4 animate-spin" />
              加载中...
            </span>
          ) : hasMore ? (
            <Button variant="ghost" size="sm" onClick={() => fetchNextPage()}>
              加载更多
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">已加载全部 {total} 个 Skill</span>
          )}
        </div>
      )}
    </div>
  );
}
